import {
  ANNOTATION_RESEARCH_NOTE_SCHEMA_VERSION,
  type AnnotationResearchNote,
  type AnnotationResearchNoteMarkdownContext,
  type AnnotationResearchNoteState,
  type AnnotationResearchNoteTarget,
  type CreateAnnotationResearchNoteInput,
} from '../types/annotationResearchNoteTypes'

export { ANNOTATION_RESEARCH_NOTE_SCHEMA_VERSION }
export type {
  AnnotationResearchNote,
  AnnotationResearchNoteMarkdownContext,
  AnnotationResearchNotePassageSnapshot,
  AnnotationResearchNoteState,
  AnnotationResearchNoteTarget,
  AnnotationResearchNoteTargetKind,
  CreateAnnotationResearchNoteInput,
} from '../types/annotationResearchNoteTypes'

export const ANNOTATION_RESEARCH_NOTE_STATES: ReadonlyArray<{
  value: AnnotationResearchNoteState
  label: string
}> = [
  { value: 'observation', label: 'Observation' },
  { value: 'question', label: 'Question' },
  { value: 'argument', label: 'Argument' },
  { value: 'citation-ready', label: 'Citation-ready' },
]

const nowIso = (): string => new Date().toISOString()

const createNoteId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `annotation-note-${crypto.randomUUID()}`
  }
  return `annotation-note-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

const normalizeKeywords = (keywords: readonly string[] = []): string[] => Array.from(new Set(
  keywords.map(keyword => keyword.trim()).filter(Boolean),
))

const normalizeOptionalText = (value: string | undefined): string | undefined => {
  const normalized = value?.trim()
  return normalized || undefined
}

const assertNoteTarget = (target: AnnotationResearchNoteTarget): void => {
  if (!target.annotationId.trim()) {
    throw new Error('A research note requires a stable annotation id')
  }
  if (target.kind === 'speaker' && target.logicalId !== undefined && !target.logicalId.trim()) {
    throw new Error('A speaker note logical id may not be blank')
  }
}

export const createAnnotationResearchNote = (
  input: CreateAnnotationResearchNoteInput,
): AnnotationResearchNote => {
  const timestamp = input.now || nowIso()
  const bodyMarkdown = input.bodyMarkdown.trim()
  if (!input.chapterId.trim() || !input.sourceWitnessId.trim()) {
    throw new Error('A research note requires a chapter and source witness')
  }
  if (!bodyMarkdown) {
    throw new Error('A research note may not be empty')
  }
  assertNoteTarget(input.target)
  return {
    id: input.id || createNoteId(),
    schemaVersion: ANNOTATION_RESEARCH_NOTE_SCHEMA_VERSION,
    chapterId: input.chapterId,
    ...(normalizeOptionalText(input.bookId) ? { bookId: normalizeOptionalText(input.bookId) } : {}),
    sourceWitnessId: input.sourceWitnessId,
    target: {
      ...input.target,
      annotationId: input.target.annotationId.trim(),
      ...(normalizeOptionalText(input.target.logicalId)
        ? { logicalId: normalizeOptionalText(input.target.logicalId) }
        : {}),
    },
    ...(normalizeOptionalText(input.title) ? { title: normalizeOptionalText(input.title) } : {}),
    bodyMarkdown,
    researchState: input.researchState || 'observation',
    keywords: normalizeKeywords(input.keywords),
    passageSnapshot: input.passageSnapshot,
    createdAt: timestamp,
    updatedAt: timestamp,
  }
}

export const updateAnnotationResearchNote = (
  note: AnnotationResearchNote,
  updates: Partial<Pick<AnnotationResearchNote, 'title' | 'bodyMarkdown' | 'researchState' | 'keywords'>>,
  updatedAt = nowIso(),
): AnnotationResearchNote => {
  const bodyMarkdown = (updates.bodyMarkdown ?? note.bodyMarkdown).trim()
  if (!bodyMarkdown) {
    throw new Error('A research note may not be empty')
  }
  const title = normalizeOptionalText(updates.title ?? note.title)
  return {
    ...note,
    ...(title ? { title } : {}),
    ...(!title && note.title ? { title: undefined } : {}),
    bodyMarkdown,
    researchState: updates.researchState || note.researchState,
    keywords: normalizeKeywords(updates.keywords || note.keywords),
    updatedAt,
  }
}

export const archiveAnnotationResearchNote = (
  note: AnnotationResearchNote,
  archivedAt = nowIso(),
): AnnotationResearchNote => ({
  ...note,
  archivedAt,
  updatedAt: archivedAt,
})

export const annotationResearchNoteTargetsMatch = (
  left: AnnotationResearchNoteTarget,
  right: AnnotationResearchNoteTarget,
): boolean => {
  if (left.kind !== right.kind) {
    return false
  }
  if (left.logicalId && right.logicalId) {
    return left.logicalId === right.logicalId
  }
  return left.annotationId === right.annotationId
}

export const getAnnotationResearchNotesForTarget = (
  notes: readonly AnnotationResearchNote[],
  target: AnnotationResearchNoteTarget,
  includeArchived = false,
): AnnotationResearchNote[] => notes
  .filter(note => (includeArchived || !note.archivedAt) && annotationResearchNoteTargetsMatch(note.target, target))
  .slice()
  .sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id))

const quoteMarkdown = (text: string): string => text
  .split(/\r?\n/u)
  .map(line => `> ${line}`.trimEnd())
  .join('\n')

const titleForNote = (note: AnnotationResearchNote): string => (
  note.title || `${note.target.kind[0].toUpperCase()}${note.target.kind.slice(1)} observation`
)

const formatTargetKind = (kind: AnnotationResearchNoteTarget['kind']): string => (
  kind === 'coreference' ? 'Coreference' : `${kind[0].toUpperCase()}${kind.slice(1)}`
)

export const renderAnnotationResearchNoteMarkdown = (
  note: AnnotationResearchNote,
  context: AnnotationResearchNoteMarkdownContext = {},
): string => {
  const units = note.passageSnapshot.unitLabels.join('–')
  const sourceParts = [context.workLabel, context.witnessLabel, units].filter(Boolean)
  const targetParts = [
    `${formatTargetKind(note.target.kind)}: ${note.target.characterLabel || note.target.characterId || 'unassigned'}`,
    note.target.annotationLabel,
  ].filter(Boolean)
  const metadata = [
    `Research state: ${note.researchState}`,
    note.keywords.length > 0 ? `Keywords: ${note.keywords.join(', ')}` : '',
  ].filter(Boolean).join(' · ')
  return [
    `### ${titleForNote(note)}`,
    '',
    quoteMarkdown(note.passageSnapshot.quotedText),
    sourceParts.length > 0 ? `> — ${context.workLabel ? `*${context.workLabel}*` : ''}${context.workLabel && sourceParts.length > 1 ? ', ' : ''}${sourceParts.slice(context.workLabel ? 1 : 0).join(', ')}` : '',
    targetParts.length > 0 ? `> ${targetParts.join(' · ')}` : '',
    '',
    note.bodyMarkdown,
    '',
    `*${metadata}*`,
  ].filter((line, index, lines) => line !== '' || (index > 0 && lines[index - 1] !== '')).join('\n').trim()
}
